import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  output,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { ControlChange } from '../../base/control-change-event';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxCheckboxIndeterminate } from './checkbox-indeterminate';

export interface CheckboxOption {
  label: string;
  value: string | number;
  checked?: boolean;
  /** Whether this option is in indeterminate (partially checked) state */
  indeterminate?: boolean;
}

let nextUniqueId = 0;

/**
 * Harmony Checkbox Component
 *
 * A checkbox component following Moon Design System specifications with:
 *
 * - Three visual states: unchecked, checked, indeterminate
 * - Full Angular Forms integration via ControlValueAccessor
 * - WCAG 2.1 AA accessibility compliance
 * - Keyboard navigation (Tab, Space)
 * - Design token-based styling
 *
 * @example
 *   ```html
 *   <!-- Two-way binding with selectedValues -->
 *   <hx-checkbox
 *     [options]="[{label: 'Accept terms', value: 'accept'}]"
 *     [(selectedValues)]="selectedValues">
 *   </hx-checkbox>
 *
 *   <!-- Forms integration -->
 *   <hx-checkbox
 *     [options]="checkboxOptions"
 *     [formControl]="checkboxControl">
 *   </hx-checkbox>
 *   ```;
 */
@Component({
  selector: 'hx-checkbox',
  templateUrl: './checkbox.html',
  styleUrls: ['./checkbox.css', '../../styles/form-control-tokens.css'],
  standalone: true,
  imports: [HxCheckboxIndeterminate],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.hx-checkbox-host]': 'true',
    '[attr.id]': 'id()',
  },
})
export class HxCheckbox extends HxBaseControl {
  /** Reference to the native input element */
  protected readonly _inputElement = viewChild.required<ElementRef<HTMLInputElement>>('input');

  /** Array of checkbox options */
  readonly options = input<CheckboxOption[]>([]);

  /** Selected values for options array */
  readonly selectedValues = input<(string | number)[]>([]);

  /** Output for selected values changes */
  readonly selectedValuesChange = output<(string | number)[]>();

  /** Custom CSS class for styling overrides */
  readonly className = input<string>('');

  /** Event emitted when checked state changes - emits ControlChange<boolean> */
  readonly change = output<ControlChange<boolean>>();

  /**
   * Handles keydown events on checkbox inputs to allow toggling via Enter key.
   * Native checkboxes only toggle on Space; Enter provides consistent keyboard interaction across
   * assessment controls (radio buttons, chips, and checkboxes).
   *
   * @internal
   */
  _handleInputKeydown(optionValue: string | number, event: KeyboardEvent): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const input = event.target as HTMLInputElement;
      input.click();
    }
  }

  /**
   * Handles change events for option checkboxes
   *
   * @internal
   */
  _handleOptionChange(optionValue: string | number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const isChecked = input.checked;

    let newSelectedValues: (string | number)[];
    if (isChecked) {
      newSelectedValues = [...this.selectedValues(), optionValue];
    } else {
      newSelectedValues = this.selectedValues().filter((v) => v !== optionValue);
    }

    this.selectedValuesChange.emit(newSelectedValues);
    this.notifyChange(newSelectedValues);
    this.markTouched();
  }

  /**
   * Check if an option is selected
   *
   * @internal
   */
  _isOptionSelected(optionValue: string | number): boolean {
    return this.selectedValues().includes(optionValue);
  }

  /** Focuses the checkbox input element */
  focus(): void {
    this._inputElement().nativeElement.focus();
  }

  // ===== HxBaseControl Overrides =====

  /** Sets the value from form model (ControlValueAccessor) */
  protected _setValue(value: any): void {
    // The checked state is managed via input binding
    // Forms will sync via writeValue -> this method
  }

  /** Validates the checkbox value (for required validation) */
  protected _validate(control: AbstractControl): ValidationErrors | null {
    // If required and no values selected, return validation error
    if (this.required() && this.selectedValues().length === 0) {
      return { required: true };
    }
    return null;
  }
}
